import { useEffect, useState } from "react";
import { toast } from "sonner";
import { rpc } from "../../../lib/rpc";
import { enqueueRpc } from "../../../lib/enqueueRpc";
import { hasQuantity } from "../../../lib/quantityInput";
import { useFlowOperationId } from "../../../hooks/useFlowOperationId";
import { FlowShell } from "../../../components/mobile/FlowShell";
import { QuantityStepper } from "../../../components/mobile/QuantityStepper";
import { FlowPhotoSlot } from "../../../components/mobile/FlowPhotoSlot";
import { PositionPicker } from "../../../components/mobile/PositionPicker";
import { ScanBox } from "../../../components/mobile/ScanBox";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

export default function EntsorgungFlow() {
    const flow = useFlowOperationId();
    const [step, setStep] = useState(1);
    const [mode, setMode] = useState("article"); // 'article' | 'asset'
    const [positions, setPositions] = useState([]);
    const [articles, setArticles] = useState({});
    const [compartments, setCompartments] = useState({});
    const [positionId, setPositionId] = useState("");
    const [scanMatches, setScanMatches] = useState(null);
    const [assetId, setAssetId] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [reason, setReason] = useState("disposal");
    const [comment, setComment] = useState("");
    const [photoId, setPhotoId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        Promise.all([
            rpc.get("/master/stock-positions"),
            rpc.get("/master/articles"),
            rpc.get("/master/compartments"),
        ]).then(
            ([p, a, c]) => {
                if (p.ok) setPositions(p.data);
                if (a.ok) setArticles(Object.fromEntries(a.data.map((x) => [x.id, x])));
                if (c.ok) setCompartments(Object.fromEntries(c.data.map((x) => [x.id, x])));
            }
        );
    }, []);

    const submit = async () => {
        if (!photoId) return toast.error("Foto-Nachweis für Entsorgung fehlt.");
        const body = {
            client_operation_id: flow.clientOperationId,
            type: "disposal",
            reason,
            comment,
            evidence_photo_id: photoId,
        };
        if (mode === "asset") {
            if (!assetId) return toast.error("Interne Kennung nötig.");
            body.asset_id = assetId;
        } else {
            if (!positionId) return toast.error("Position nötig.");
            const pos = positions.find((p) => p.id === positionId);
            if (!pos) return toast.error("Position nicht gefunden.");
            if (!hasQuantity(quantity)) return toast.error("Menge eingeben.");
            // #118: Backend verlangt article_id bei Bulk-Disposal.
            body.article_id = pos.article_id;
            body.from_position_id = positionId;
            body.quantity = quantity;
        }
        setSubmitting(true);
        const res = await enqueueRpc({
            path: "/movements",
            rpcName: "Entsorgung",
            summary: mode === "asset" ? `Asset ${assetId.slice(0, 8)}…` : `${quantity} Stk`,
            payload: body,
        });
        setSubmitting(false);
        if (!res.ok) {
            if (res.retryable) toast.warning("Offline -- Entsorgung wartet in der Queue.");
            else return toast.error(res.error);
        } else {
            toast.success("Entsorgung gebucht.");
        }
        flow.rotate();
        setStep(1);
        setPositionId("");
        setScanMatches(null);
        setAssetId("");
        setQuantity(1);
        setComment("");
        setPhotoId(null);
    };

    return (
        <FlowShell
            testId="flow-entsorgung"
            title="Entsorgung"
            kind="Step-Wizard"
            stepIndicator={`${step} / 3`}
            onSubmit={step === 3 ? submit : () => setStep(step + 1)}
            submitLabel={step === 3 ? "Buchen" : "Weiter"}
            submitDisabled={submitting || (step === 2 && mode === "article" && !hasQuantity(quantity)) || (step === 3 && !photoId)}
            submitting={submitting}
        >
            {step === 1 ? (
                <>
                    <ScanBox
                        testId="ent-scan"
                        placeholder="Artikel, Fach oder Gegenstand scannen"
                        onResolved={(raw, resolved) => {
                            if (resolved?.kind === "asset") {
                                setMode("asset");
                                setAssetId(resolved.id);
                                setScanMatches(null);
                                toast.success("Ausleihgegenstand übernommen.");
                                return;
                            }
                            const matches = resolved?.kind === "article"
                                ? positions.filter((position) => position.article_id === resolved.id)
                                : resolved?.kind === "compartment"
                                    ? positions.filter((position) => position.compartment_id === resolved.id)
                                    : [];
                            if (matches.length === 1) {
                                setMode("article");
                                setPositionId(matches[0].id);
                                setScanMatches(null);
                                toast.success("Position aus Scan übernommen.");
                            } else if (matches.length > 1) {
                                setMode("article");
                                setPositionId("");
                                setScanMatches(matches.map((position) => position.id));
                                toast.warning(`${matches.length} Positionen gefunden -- direkt auswählen.`);
                            } else {
                                toast.warning(`Kein Bestand zu Code ${raw}.`);
                            }
                        }}
                    />
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setMode("article")}
                            className={`pw-tap flex-1 rounded-md border px-3 py-3 text-sm ${mode === "article" ? "border-primary text-foreground" : "border-border text-muted-foreground"}`}
                            data-testid="ent-mode-article"
                        >
                            Bulk (Artikel)
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode("asset")}
                            className={`pw-tap flex-1 rounded-md border px-3 py-3 text-sm ${mode === "asset" ? "border-primary text-foreground" : "border-border text-muted-foreground"}`}
                            data-testid="ent-mode-asset"
                        >
                            Ausleihgegenstand
                        </button>
                    </div>
                    {mode === "article" ? (
                        <div className="flex flex-col gap-2">
                            {scanMatches ? (
                                <p className="text-xs text-primary" data-testid="ent-scan-matches">
                                    {scanMatches.length} Scan-Treffer – Position wählen
                                </p>
                            ) : null}
                            <PositionPicker
                                value={positionId}
                                onChange={(value) => {
                                    setPositionId(value);
                                    setScanMatches(null);
                                }}
                                positions={scanMatches ? positions.filter((p) => scanMatches.includes(p.id)) : positions}
                                articles={articles}
                                compartments={compartments}
                                testId="ent-position"
                                placeholder="Position wählen"
                            />
                        </div>
                    ) : (
                        <Input
                            value={assetId}
                            onChange={(e) => setAssetId(e.target.value)}
                            placeholder="Interne Kennung"
                            className="h-12"
                            aria-label="Interne Kennung"
                    data-testid="ent-asset-id"
                        />
                    )}
                </>
            ) : null}

            {step === 2 ? (
                <>
                    {mode === "article" ? (
                        <>
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Menge</Label>
                            <QuantityStepper value={quantity} onChange={setQuantity} min={1} testId="ent-qty" />
                        </>
                    ) : null}
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Grund</Label>
                    <Select value={reason} onValueChange={setReason}>
                        <SelectTrigger className="h-12" data-testid="ent-reason">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="disposal">Entsorgung</SelectItem>
                            <SelectItem value="defect">Defekt</SelectItem>
                            <SelectItem value="damage">Beschädigt</SelectItem>
                        </SelectContent>
                    </Select>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kommentar</Label>
                    <Input value={comment} onChange={(e) => setComment(e.target.value)} className="h-12" aria-label="Kommentar" data-testid="ent-comment" />
                </>
            ) : null}

            {step === 3 ? (
                <>
                    <div className="rounded-lg border border-destructive/40 bg-[rgb(var(--destructive-bg))] p-3 text-sm">
                        Foto-Nachweis ist bei Entsorgung Pflicht.
                    </div>
                    <FlowPhotoSlot
                        sessionKey={flow.clientOperationId}
                        label="Entsorgungs-Nachweis"
                        entityType="movement"
                        purpose="disposal_evidence"
                        required={true}
                        isEvidence={true}
                        requireSync={true}
                        value={photoId}
                        onChange={setPhotoId}
                        helpText="Pflicht-Foto, wird an die Buchung fest gebunden und ist später gegen Löschung geschützt."
                        testId="ent-photo"
                    />
                </>
            ) : null}
        </FlowShell>
    );
}
